import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';

export enum StepState {
  NOT_STARTED = 'NOT_STARTED',
  /**
   * The run's jobs are enqueued but none has started — they wait on a gate (answer extraction waits for the
   * structure confirmation, which is a teacher). The UI reads this as not-yet-running, while the gates that ask "has
   * this step been started" (`anySetupStepOngoing`, the re-grade check, `exam-lock`) count it like IN_PROGRESS.
   * Flips to IN_PROGRESS when the first unit starts (`markUnitStarted`).
   */
  QUEUED = 'QUEUED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  ERROR = 'ERROR',
}

registerEnum({ enum: StepState, name: 'StepState' });

/**
 * The exam collection, imported lazily.
 *
 * The dynamic import is not incidental: `exam.js` imports this module for `StepState` and the progress
 * entities, so a static import here would be a cycle, and one that runs at decorator-evaluation time. Every
 * write below went through its own copy of these two lines; one helper is the same thing said once.
 */
async function exams() {
  const { Exam } = await import('./exam.js');
  return getCollection(Exam);
}

/** A counter step's "nothing done yet" document, minus `reupload` — that belongs to the run. */
function stepProgressDefault(step: StepType, examID: string, message: string): Record<string, unknown> {
  return {
    _class: step === 'task' ? TaskExtractionProgress._class : AnswerExtractionProgress._class,
    examID,
    message,
    status: StepState.NOT_STARTED,
    total: 0,
    finishedUnits: [],
    lowConfRegions: [],
    errorRegions: [],
    ...(step === 'answer' ? { errorSubmissions: [] } : {}),
  };
}

/**
 * Resets a counter step to "nothing done yet". Called once per exam, when the pipeline's steps are created.
 *
 * FIELD-WISE RATHER THAN REPLACING THE DOCUMENT, and that is the whole reason this no longer goes through a
 * whole-record `$set`: `reupload` is written by `beginProgressRun` BEFORE the step's job is enqueued, while
 * this runs inside the handler, after. Replacing the record here would erase how the run was triggered, and
 * the announcement would then word a re-upload as a first extraction.
 */
export async function createStep(type: string, examID: string, message = ''): Promise<boolean> {
  if (type !== 'task' && type !== 'answer') throw new Error(`Unknown extraction type: ${type}`);
  if (!examID) throw new Error('Cannot update extraction progress without examID');

  const examCollection = await exams();
  const path = progressStatePath(type);

  await seedProgressRecord(examID, type);
  // EVERYTHING EXCEPT THE STATUS. A step that has started must never read as pending again — this runs INSIDE the
  // handler, so by the time it does, the claim has already marked the step running, and resetting the status here
  // put the bar back to NOT_STARTED for the length of one write. Counters are a different matter: a re-extraction
  // genuinely starts them over, which is what this call is for.
  const { status: _status, ...fields } = stepProgressDefault(type, examID, message);
  await examCollection.updateOne(
    { _id: examID },
    { $set: Object.fromEntries(Object.entries(fields).map(([field, value]) => [`${path}.${field}`, value])) },
  );
  return true;
}

/**
 * The steps whose progress is a plain counter over its own documents.
 *
 * The master solution and the grading scheme are NOT here: each has ONE state that both extraction and
 * generation write (see {@link SolutionArtifact}).
 */
export type StepType = 'task' | 'answer';

/** The two artifacts that can be produced either by extraction (uploaded file) or by generation. */
export type SolutionArtifact = 'masterSolution' | 'gradingScheme';

/** Anything that has a progress state on the exam, addressed uniformly by the helpers below. */
export type ProgressKey = StepType | SolutionArtifact;

export function isSolutionArtifact(key: ProgressKey): key is SolutionArtifact {
  return key === 'masterSolution' || key === 'gradingScheme';
}

/** Where a progress state lives on the exam document. */
function progressStatePath(key: ProgressKey): string {
  switch (key) {
    case 'task':
      return 'examState.taskExtractionProgress';
    case 'answer':
      return 'examState.answerExtractionProgress';
    case 'masterSolution':
      return 'examState.masterSolutionProgress';
    case 'gradingScheme':
      return 'examState.gradingSchemeProgress';
  }
}

// --- Runs ---
//
// A RUN is one pass of a step: this structure extraction, this uploaded grading scheme being read, this
// generation the teacher asked for, this round of confirmed submissions.
//
// WHICH PASS IS CURRENT IS NOT RECORDED HERE, AND IS NOT RECORDED ANYWHERE. A step is running while the exam
// has a live job of that step's types (`anyLiveJobForTypes` / `STEP_JOB_TYPES`) and finished when it does not.
// This record used to carry a `runId`, then the jobs carried a `batchKey`; both were names for that same
// predicate, and the `runId` version was a category mismatch as well — everything else in here is cumulative and
// deliberately run-agnostic (`total` is counted from the collection, `finishedUnits` and `createdForTasks` span
// every pass), while `runId` had one slot for a thing two passes can legitimately ask about at once. Overwriting
// it silently retired a run still in flight.
//
// What DOES belong to the pass and stays: `reupload`, which describes the trigger, and `changedTasks` /
// `pointAdjustments`, which describe what this pass altered. They are written at different moments and that
// difference matters — `reupload` at the DISPATCH (`beginProgressRun`, the only thing that knows how the run was
// triggered), the other two when the pass actually begins (`startSolutionProgress`, because a solution record is
// shared by three producers and one of them may still be mid-pass when the next is queued). All are read when the
// step closes.

/** The progress record of `key`, read straight off an exam document's `examState`. */
export function progressRecordOf(examState: unknown, key: ProgressKey): Record<string, any> | null {
  const state = examState as Record<string, any> | null | undefined;
  if (!state) return null;
  switch (key) {
    case 'task':
      return state.taskExtractionProgress ?? null;
    case 'answer':
      return state.answerExtractionProgress ?? null;
    case 'masterSolution':
      return state.masterSolutionProgress ?? null;
    case 'gradingScheme':
      return state.gradingSchemeProgress ?? null;
  }
}

/**
 * Seeds a progress record when it is missing or explicitly `null`, for either kind of state. Never touches a record
 * that already exists.
 *
 * Needed because a run opens BEFORE the step that would have created the record: `beginProgressRun('task')` runs at
 * the enqueue site, `createStep('task')` inside the handler minutes later. `$set` on a field of a null element fails
 * in Mongo, so both kinds have to be seedable, not just the solution artifacts.
 *
 * EXPORTED FOR THE STRUCTURE STEP, which wants the whole pipeline's records to exist so the UI can show every step
 * from the start. That used to be `createStep` / `initSolutionProgress`, which RESET them — and reset is only correct
 * on a record nobody else has opened. Uploading student answers on an exam with no structure yet opens the answer run
 * (IN_PROGRESS, its total) while its rows wait for the structure to be confirmed; the structure step then ran and put
 * that record back to NOT_STARTED with total 0, so the bar fell back to pending and — worse — the run could never
 * close, because `runStepCompletion` refuses a record that is neither IN_PROGRESS nor COMPLETED. The answers were
 * extracted and the step stayed unfinished forever.
 */
export async function seedProgressRecord(examID: string, key: ProgressKey): Promise<void> {
  if (isSolutionArtifact(key)) {
    await ensureSolutionProgress(examID, key);
    return;
  }
  const examCollection = await exams();
  // `{ field: null }` matches both an explicit null and a missing field.
  await examCollection.updateOne({ _id: examID, [progressStatePath(key)]: null } as any, {
    $set: { [progressStatePath(key)]: { ...stepProgressDefault(key, examID, ''), reupload: false } },
  });
}

/**
 * Records that a new run of `key` is starting. Call it immediately before enqueuing that run's jobs.
 *
 * Deliberately does NOT touch status, totals or the done-sets: what "starting" means differs per step (a
 * generation keeps its finished units, a re-extraction drops them) and those calls already exist. `reupload` is now
 * ALL it owns — not a run id (which pass is current is not recorded anywhere, it is whether the exam has a live job
 * of this step, see the note above), and no longer the per-pass fields either: this runs before the rows exist, and
 * a solution record's pass may not have begun for minutes yet. `startSolutionProgress` clears those.
 *
 * `reupload` describes the TRIGGER, not the state, so it is recorded here rather than derived later, when the
 * run closes and nothing remembers how it started. It defaults to "this step already ran to a conclusion
 * once", which is exactly what a repeat is; pass it explicitly where the caller knows better.
 */
export async function beginProgressRun(
  examID: string,
  key: ProgressKey,
  options: { reupload?: boolean } = {},
): Promise<void> {
  if (!examID) throw new Error('examID required');
  await seedProgressRecord(examID, key);

  const examCollection = await exams();
  const previous = await examCollection.findOne({ _id: examID }, { projection: { examState: 1 } });
  const status = progressRecordOf((previous as any)?.examState, key)?.status;

  const reupload = options.reupload ?? (status === StepState.COMPLETED || status === StepState.ERROR);
  // THE PER-PASS FIELDS ARE NOT CLEARED HERE, and that is the point of where they moved to. This runs at DISPATCH
  // time, while a solution record is shared by three producers — so clearing them here wiped the sets of a pass
  // that was still running. `startSolutionProgress` owns them now, because declaring the record IN_PROGRESS is
  // what "this pass has begun" means; see the note there for what the dispatch-time clear cost.
  await examCollection.updateOne({ _id: examID } as any, {
    $set: { [`${progressStatePath(key)}.reupload`]: reupload },
  });
}

export async function getProgressByExam(examID: string, step: StepType) {
  const examCollection = await exams();
  const doc = await examCollection.findOne({ _id: examID });
  if (!doc?.examState) return null;
  const pathMap: Record<StepType, any> = {
    task: doc.examState?.taskExtractionProgress,
    answer: doc.examState?.answerExtractionProgress,
  };
  const payload = pathMap[step];
  if (!payload) return null;
  switch (step) {
    case 'task':
      return new TaskExtractionProgress(payload);
    case 'answer':
      return new AnswerExtractionProgress(payload);
  }
}

/** How many units of `key` are done. The one thing every reader wanted from the old `finished` counter. */
export function finishedUnitCount(examState: unknown, key: ProgressKey): number {
  const record = progressRecordOf(examState, key);
  const units = isSolutionArtifact(key) ? record?.createdForTasks : record?.finishedUnits;
  return Array.isArray(units) ? units.length : 0;
}

export async function addErrorSubmissionByExam(examID: string, submissionId: string): Promise<boolean> {
  if (!examID) throw new Error('examID required');
  const examCollection = await exams();
  const progressPath = `examState.answerExtractionProgress.errorSubmissions`;
  await examCollection.updateOne({ _id: examID }, { $addToSet: { [progressPath]: submissionId } });
  return true;
}

export async function addLowConfRegionByExam(
  examID: string,
  key: ProgressKey,
  region: LowConfRegion,
): Promise<boolean> {
  if (!examID) throw new Error('examID required');
  const examCollection = await exams();
  if (isSolutionArtifact(key)) await ensureSolutionProgress(examID, key);
  await examCollection.updateOne(
    { _id: examID },
    { $addToSet: { [`${progressStatePath(key)}.lowConfRegions`]: region.toObject() } },
  );
  return true;
}

export async function addErrorRegionByExam(examID: string, key: ProgressKey, region: ErrorRegion): Promise<boolean> {
  if (!examID) throw new Error('examID required');
  const examCollection = await exams();
  if (isSolutionArtifact(key)) await ensureSolutionProgress(examID, key);
  await examCollection.updateOne(
    { _id: examID },
    { $addToSet: { [`${progressStatePath(key)}.errorRegions`]: region.toObject() } },
  );
  return true;
}

// --- Units ---
//
// Progress is "which units are done", never "how many" — `start` pulls the unit out of the done set,
// `finish` puts it back. Both writes are idempotent and neither reads the current value, which is what makes
// the pair correct under every repeat the product allows (a re-upload, a re-generation, a refine of two
// tasks, a requeued job, a retry of something that had already succeeded) without any of those being named
// anywhere. Arithmetic has neither property, so the counter needed a different correction per case.

/**
 * Marks a unit as picked up, and therefore NOT done — call it at the start of the unit's work.
 *
 * The pull is the half the old counter could not express: a unit being redone was already counted, so the
 * count had to be walked back by whoever knew that (`incrementFinishedByExam(-n)`, `dropSolutionTasks`,
 * `$max(0, $subtract …)`) — three corrections at three call sites that each had to know they were a repeat.
 * A unit that removes itself needs no caller to know anything; doing it the first time pulls an id that is
 * not there, which is free. For `answer` it also clears the unit's error mark, for the same reason.
 */
export async function markUnitStarted(examID: string, key: ProgressKey, unitId: string): Promise<void> {
  if (!examID) throw new Error('examID required');
  if (isSolutionArtifact(key)) {
    await markSolutionTaskStarted(examID, key, [unitId]);
    return;
  }
  await seedProgressRecord(examID, key);
  const examCollection = await exams();
  const path = progressStatePath(key);
  // A unit starting is the end of "queued" — only that status, so a record that is already running or closed is
  // never touched.
  await examCollection.updateOne(
    { _id: examID, [`${path}.status`]: StepState.QUEUED } as any,
    { $set: { [`${path}.status`]: StepState.IN_PROGRESS } },
  );
  await examCollection.updateOne(
    { _id: examID },
    {
      $pull: {
        [`${path}.finishedUnits`]: unitId,
        ...(key === 'answer' ? { [`${path}.errorSubmissions`]: unitId } : {}),
      },
    },
  );
}

/**
 * Records one unit of work as done, whichever kind of state it belongs to — by id, so re-running a unit
 * cannot inflate anything and a refine can put exactly the units it is redoing back into the outstanding
 * set. For a solution artifact the id is the top-level task's `_id`; for a counter step it is the task or
 * submission the unit was.
 */
export async function markUnitFinished(examID: string, key: ProgressKey, unitId: string): Promise<void> {
  if (!examID) throw new Error('examID required');
  if (isSolutionArtifact(key)) {
    await markSolutionTasksDone(examID, key, [unitId]);
    return;
  }
  await seedProgressRecord(examID, key);
  const examCollection = await exams();
  await examCollection.updateOne(
    { _id: examID },
    { $addToSet: { [`${progressStatePath(key)}.finishedUnits`]: unitId } },
  );
}

/**
 * Empties a counter step's done set — the counter-step twin of `clearSolutionTasks`.
 *
 * For a run that REDOES every unit rather than adding to them. Structure extraction is the case: it rebuilds
 * the task tree from scratch, so the previous run's units do not describe anything that still exists, and
 * `$addToSet` would otherwise carry them forward and report more finished units than the new total has.
 *
 * Takes `StepType` rather than `ProgressKey` so this is a COMPILE error for a solution artifact instead of a
 * silent no-op: those record their units in `createdForTasks`, and clearing `finishedUnits` there would touch
 * a field nothing reads.
 */
export async function clearFinishedUnits(examID: string, step: StepType): Promise<void> {
  if (!examID) throw new Error('examID required');
  await seedProgressRecord(examID, step);
  const examCollection = await exams();
  await examCollection.updateOne({ _id: examID }, { $set: { [`${progressStatePath(step)}.finishedUnits`]: [] } });
}

export async function setStatusByExam(
  examID: string,
  step: StepType,
  status: StepState,
  message = '',
): Promise<boolean> {
  if (!examID) throw new Error('examID required');
  const examCollection = await exams();
  const progressPath = `examState.${step}ExtractionProgress`;
  await examCollection.updateOne(
    { _id: examID },
    {
      $set: {
        [`${progressPath}.status`]: status,
        [`${progressPath}.message`]: message,
      },
    },
  );
  return true;
}

// --- Solution progress (master solution / grading scheme): ONE state per artifact ---
//
// Extraction and generation used to keep separate states (`msExtractionProgress` vs
// `masterSolutionGenerationState`), which forced every reader to first decide which one to look at —
// usually off `masterSolutionUploaded`, which describes provenance, not what is running. Both write here
// now; `producer` says which of them did, so the UI can still word it as "detected" vs "generated".

function solutionProgressDefault(): Record<string, unknown> {
  return {
    _class: SolutionProgress._class,
    status: StepState.NOT_STARTED,
    producer: null,
    message: '',
    total: 0,
    startedForTasks: [],
    createdForTasks: [],
    lowConfRegions: [],
    errorRegions: [],
    pointAdjustments: [],
    changedTasks: [],
    reupload: false,
  };
}

/**
 * Seeds the state when it is missing or explicitly `null`, without ever touching one that exists.
 *
 * A declared-but-unset entity field persists as `null`, and null is not absent for Mongo: `$set` on
 * `<null>.status` fails ("Cannot create field 'status' in element"), and `$addToSet` on a null array
 * fails too. Every mutating helper below seeds first, so no caller has to know.
 */
async function ensureSolutionProgress(examID: string, artifact: SolutionArtifact): Promise<void> {
  const examCollection = await exams();
  // `{ field: null }` matches both an explicit null and a missing field.
  await examCollection.updateOne({ _id: examID, [progressStatePath(artifact)]: null } as any, {
    $set: { [progressStatePath(artifact)]: solutionProgressDefault() },
  });
}

async function patchSolutionProgress(
  examID: string,
  artifact: SolutionArtifact,
  patch: Record<string, unknown>,
): Promise<void> {
  if (!examID) throw new Error('examID required');
  await ensureSolutionProgress(examID, artifact);
  const examCollection = await exams();
  const path = progressStatePath(artifact);
  await examCollection.updateOne({ _id: examID } as any, {
    $set: Object.fromEntries(Object.entries(patch).map(([field, value]) => [`${path}.${field}`, value])),
  });
}

/** Resets the state to "nothing done yet". Called once per exam, when the pipeline's steps are created. */
/**
 * Opens a run: IN_PROGRESS, its unit total, which producer it is, and an empty set of per-pass fields.
 *
 * The task lists are deliberately KEPT — a generation that fills the gaps of an uploaded solution continues an
 * existing count, and a re-run must not lose what is already done. Clear them explicitly (`clearSolutionTasks` /
 * `dropSolutionTasks`) when the units really are being redone.
 *
 * THE PER-PASS FIELDS ARE CLEARED HERE, and this is the only place they are. They used to be cleared by
 * `beginProgressRun`, which runs at DISPATCH time — and one record is shared by THREE producers, so that cleared
 * fields belonging to a pass that was still running. A `gsRefine` enqueued while an uploaded scheme was being
 * extracted is the case, and it is the normal case rather than a corner: the refine's own `ready()` gate holds it
 * back precisely while that extraction runs, so the clear landed minutes before the refine did anything. It cost
 * the extraction its `pointAdjustments` (the teacher never hears which points the upload changed) and its
 * `changedTasks` — and an empty `changedTasks` is read as "nothing changed", which silently cancels the re-grade
 * for an uploaded scheme that changed the rubric.
 *
 * So the clear belongs where the pass actually BEGINS, which is this call by definition: whoever declares the
 * record IN_PROGRESS is the pass that will write these fields. That is dispatch time for a generation (its opener
 * runs there, deliberately — see `openMasterSolutionGenerationRun`) and run time for a refine or an extraction,
 * which is exactly right in both cases. Being part of the same `$set` as the status leaves no window at all.
 */
export async function startSolutionProgress(
  examID: string,
  artifact: SolutionArtifact,
  options: { producer: SolutionProducer; total?: number; message?: string },
): Promise<void> {
  await patchSolutionProgress(examID, artifact, {
    status: StepState.IN_PROGRESS,
    producer: options.producer,
    // BOTH, and for the same reason: they describe ONE pass. Carrying the previous run's `changedTasks` over would
    // make a second upload look like it had changed tasks it never touched, and regrade them. Carrying its
    // `pointAdjustments` over is what made a refine of task 1 announce that it had adjusted points in subtasks of
    // task 2 — the extraction's adjustments, still sitting on the record because nothing had ever cleared them.
    changedTasks: [],
    pointAdjustments: [],
    ...(options.total != null ? { total: options.total } : {}),
    ...(options.message != null ? { message: options.message } : {}),
  });
}

export async function setSolutionStatus(
  examID: string,
  artifact: SolutionArtifact,
  status: StepState,
  message?: string,
): Promise<void> {
  await patchSolutionProgress(examID, artifact, {
    status,
    ...(message != null ? { message } : {}),
  });
}

export async function setSolutionTotal(examID: string, artifact: SolutionArtifact, total: number): Promise<void> {
  await patchSolutionProgress(examID, artifact, { total });
}

export async function setSolutionMessage(examID: string, artifact: SolutionArtifact, message: string): Promise<void> {
  await patchSolutionProgress(examID, artifact, { message });
}

/**
 * Records the task paths whose points this run changed, for the announcement to name.
 *
 * On the RECORD rather than passed along in memory, because the step that computes them and the code that
 * announces them are no longer the same call: the extraction returns `pointDiffs` inside a job handler, and
 * the run is closed afterwards by whichever transition happens to be last (`syncStepCompletion`). Writing them
 * down is what lets the announcement survive the gap — and a restart in it.
 */
export async function setSolutionPointAdjustments(
  examID: string,
  artifact: SolutionArtifact,
  taskPaths: string[],
): Promise<void> {
  await patchSolutionProgress(examID, artifact, { pointAdjustments: taskPaths });
}

/**
 * Marks units as picked up — read as "something is already running" by the MS/GS tools — and, because
 * picked up means not done, takes them back out of `createdForTasks`.
 *
 * That second half is what makes a re-generation of an already-generated node come out right without the
 * caller having to say so. `dropSolutionTasks` still exists and refine still calls it, but only so the bar
 * drops the moment the refine is requested rather than when the call actually starts; correctness no
 * longer depends on anyone remembering it.
 */
export async function markSolutionTaskStarted(
  examID: string,
  artifact: SolutionArtifact,
  taskIds: string[],
): Promise<void> {
  if (!examID) throw new Error('examID required');
  if (!taskIds.length) return;
  await ensureSolutionProgress(examID, artifact);
  const examCollection = await exams();
  const path = progressStatePath(artifact);
  await examCollection.updateOne({ _id: examID }, { $pullAll: { [`${path}.createdForTasks`]: taskIds } });
  await addSolutionTaskKeys(examID, artifact, 'startedForTasks', taskIds);
}

/** Marks units as done. Also asserts IN_PROGRESS: a unit landing means the run is live. */
export async function markSolutionTasksDone(
  examID: string,
  artifact: SolutionArtifact,
  taskIds: string[],
): Promise<void> {
  await addSolutionTaskKeys(examID, artifact, 'createdForTasks', taskIds, { status: StepState.IN_PROGRESS });
}

/**
 * Records tasks whose artifact content a write actually changed. Concurrent roots each add their own, so it
 * accumulates as a set rather than being replaced.
 */
export async function addSolutionChangedTasks(
  examID: string,
  artifact: SolutionArtifact,
  taskIds: string[],
): Promise<void> {
  await addSolutionTaskKeys(examID, artifact, 'changedTasks', taskIds);
}

// NO `clearSolutionPassFields` HERE ANY MORE. The per-pass fields (`changedTasks`, `pointAdjustments`) are cleared
// by `startSolutionProgress`, in the same write that declares the record IN_PROGRESS — a separate clear could only
// ever be called at the wrong time, which is what it was. See the note there.

/** Forgets every finished/started unit, so a fresh run counts from zero (a re-extraction). */
export async function clearSolutionTasks(examID: string, artifact: SolutionArtifact): Promise<void> {
  await patchSolutionProgress(examID, artifact, { startedForTasks: [], createdForTasks: [] });
}

/** Forgets specific units, so exactly those re-count as outstanding (a refine of some tasks). */
export async function dropSolutionTasks(examID: string, artifact: SolutionArtifact, taskIds: string[]): Promise<void> {
  if (!taskIds.length) return;
  if (!examID) throw new Error('examID required');
  await ensureSolutionProgress(examID, artifact);
  const examCollection = await exams();
  const path = progressStatePath(artifact);
  await examCollection.updateOne(
    { _id: examID } as any,
    {
      $pullAll: { [`${path}.startedForTasks`]: taskIds, [`${path}.createdForTasks`]: taskIds },
    } as any,
  );
}

/**
 * `$setUnion` on a pipeline update rather than `$addToSet`: these are the writes that run concurrently
 * (one per subtree/task), and `$ifNull` makes missing, null and array behave the same in one atomic
 * write. Order is not preserved — nothing reads these as ordered, only as sets.
 */
async function addSolutionTaskKeys(
  examID: string,
  artifact: SolutionArtifact,
  field: 'startedForTasks' | 'createdForTasks' | 'changedTasks',
  taskKeys: string[],
  extra?: Record<string, unknown>,
): Promise<void> {
  if (!examID) throw new Error('examID required');
  if (!taskKeys.length) return;
  await ensureSolutionProgress(examID, artifact);
  const { Exam } = await import('./exam.js');
  const path = `${progressStatePath(artifact)}.${field}`;
  const rawExams = await getRawCollection(Exam._collectionName);
  await rawExams.updateOne(
    { _id: examID } as any,
    [
      {
        $set: {
          [path]: { $setUnion: [{ $ifNull: [`$${path}`, []] }, taskKeys] },
          ...(extra
            ? Object.fromEntries(
                Object.entries(extra).map(([key, value]) => [`${progressStatePath(artifact)}.${key}`, value]),
              )
            : {}),
        },
      },
    ] as any,
  );
}

/**
 * Marks a run as enqueued but not started. Never downgrades a step that is already running — a re-upload dispatching
 * a second round while the first is extracting must not make the bar read queued.
 */
export async function queueStepByExam(examID: string, step: StepType, message = ''): Promise<void> {
  if (!examID) throw new Error('examID required');
  await seedProgressRecord(examID, step);
  const examCollection = await exams();
  const path = progressStatePath(step);
  await examCollection.updateOne(
    { _id: examID, [`${path}.status`]: { $ne: StepState.IN_PROGRESS } } as any,
    { $set: { [`${path}.status`]: StepState.QUEUED, [`${path}.message`]: message } },
  );
}

export async function setTotalByExam(examID: string, step: StepType, total: number): Promise<boolean> {
  if (!examID) throw new Error('examID required');
  const examCollection = await exams();
  const progressPath = `examState.${step}ExtractionProgress`;
  await examCollection.updateOne(
    { _id: examID },
    {
      // TOTAL ONLY. It used to flip the status to IN_PROGRESS as well, which made every caller that merely knows how
      // many units there will be also claim that they are being worked on — see `markStepRunning` for who owns that.
      $set: { [`${progressPath}.total`]: total },
    },
  );
  return true;
}

// `incrementTotalByExam` is gone with `incrementFinishedByExam`: `total` is now always SET from a count, so
// a second round cannot accumulate it past the truth. Accumulating it was the mirror of the `finished`
// problem — a re-upload added its submissions to a total that already included every earlier one.

export async function updateMessageByExam(examID: string, step: StepType, message: string): Promise<boolean> {
  if (!examID) throw new Error('examID required');
  const examCollection = await exams();
  const progressPath = `examState.${step}ExtractionProgress`;
  await examCollection.updateOne({ _id: examID }, { $set: { [`${progressPath}.message`]: message } });
  return true;
}

@Entity()
class BaseRegion extends MongoEntity {
  static override _class = 'BaseRegion';

  @Field(() => String)
  submissionId!: string;

  @Field(() => [String])
  taskPath!: string[];

  @Field(() => Boolean)
  additionalInfo!: boolean;

  @Field(() => String)
  boundingBoxId!: string;
}

@Entity()
export class LowConfRegion extends BaseRegion {
  static override _class = 'LowConfRegion';
  static override resolveID(data: any) {
    // Use boundingBoxId as unique identifier (or combine with submissionId if needed)
    const taskPath = Array.isArray(data?.taskPath) ? data.taskPath.join('|') : '';
    return data.boundingBoxId || `${data.submissionId || ''}-${taskPath}`;
  }
}

@Entity()
export class ErrorRegion extends BaseRegion {
  static override _class = 'ErrorRegion';
  static override resolveID(data: any) {
    // Use boundingBoxId as unique identifier (or combine with submissionId if needed)
    const taskPath = Array.isArray(data?.taskPath) ? data.taskPath.join('|') : '';
    return data.boundingBoxId || `${data.submissionId || ''}-${taskPath}`;
  }
}

@Entity()
export class BaseExtractionProgress extends MongoEntity {
  static override _class = 'BaseExtractionProgress';

  @Field(() => String)
  examID!: string;

  @Field(() => String)
  message!: string;

  @Field(() => StepState)
  status!: StepState;

  @Field(() => Int)
  total!: number;

  /**
   * WHICH units are done, not how many — the same shape `SolutionProgress` uses, and for the same reasons.
   *
   * It replaced a `finished` counter, which was only ever correct if every caller got its arithmetic right:
   * `+1` per unit, `-n` when a round was retried, `$max(0, …)` so a re-grade could not drive it negative.
   * Each is a place a re-upload, a re-generation or a requeued job miscounts, and none is recoverable once
   * wrong, because a number does not remember which unit it meant.
   *
   * A set needs no arithmetic. A unit removes itself when it STARTS and adds itself when it FINISHES, so
   * redoing something that already succeeded walks the bar back by exactly one, a crashed job simply is not
   * in the set, and running a unit twice is indistinguishable from running it once. New work is outstanding
   * by virtue of not being here. See `markUnitStarted` / `markUnitFinished`.
   *
   * The ids identify a unit of THIS step: a task `_id` for `task`, a submission id for `answer`. `total`
   * stays a number because it is set from a count, never accumulated.
   */
  @Field(() => [String])
  finishedUnits!: string[];

  @Field(() => [LowConfRegion])
  lowConfRegions!: LowConfRegion[];

  @Field(() => [ErrorRegion])
  errorRegions!: ErrorRegion[];

  /** This run repeats a step that already concluded once. Only affects how the run is announced. */
  @Field(() => Boolean)
  reupload: Maybe<boolean>;
}

/** Which producer wrote a solution progress state — extraction of an upload, or generation. */
/**
 * Who wrote the artifact this run is about.
 *
 * `refine` is here so a correction can be told apart from a delivery, and the distinction is load-bearing in two
 * places: the setup-step announcement is for background work the teacher did not ask about one-by-one, so a refine
 * must NOT produce one (it was requested in chat and answered in the same turn — announcing again told the teacher
 * the scheme had been "fully generated" when one task had been corrected); and `artifactExtractionRunning` gates only
 * on `extraction`, so a running refine correctly does not read as an upload in flight.
 */
export type SolutionProducer = 'extraction' | 'generation' | 'refine';

/**
 * The single progress state of a master solution or a grading scheme, written by BOTH extraction and
 * generation.
 *
 * ONE UNIT = ONE GRADABLE TASK NODE (see isGradable/isSolutionUnitRoot's "purely a task frame" exclusion),
 * keyed by that node's own `_id` — not the top-level main task it lives under. A generation/refine call is
 * still addressed to a whole root subtree (one LLM call covers it), but rarely targets every node in it: a
 * refine of one subtask only ever rewrites that subtask, so counting nodes (not roots) is what lets the
 * bar — and a per-box "is this being (re)generated" read of `startedForTasks`/`createdForTasks` — move by
 * exactly the node(s) a call actually touched, instead of crediting (or spinning) every sibling under the
 * same root. Sharing the unit is what lets extraction and generation share the state — an upload covering
 * two of three nodes and a generation filling the third add up to 3/3 instead of two incomparable
 * fractions.
 *
 * Done-ness is a set of node keys rather than a counter: it makes the writes idempotent (each producer
 * runs its units concurrently and may re-run one) and lets a refine put specific units back into the
 * outstanding set. `total` is the number of units THIS run counts (every gradable node, for both
 * producers — see `solutionUnitNodeCount` / `countSolutionUnitNodes`), so `createdForTasks.length / total`
 * is the progress either way.
 *
 * Two fields are producer-specific, by design rather than by omission: regions only ever come from an
 * extraction (a generation has no OCR to be uncertain about), and `startedForTasks` only from a
 * generation, which claims its targeted nodes before a long LLM call — an extraction's units are short
 * enough that `status` alone answers "is it running?" (so `createdForTasks` never grows during one; only
 * `total` reflects it, for whatever an eventual extraction-side per-node signal might want later).
 */
@Entity()
export class SolutionProgress extends AdornisEntity {
  static override _class = 'SolutionProgress';

  @Field(() => StepState)
  status!: StepState;

  @Field(() => String)
  producer: Maybe<SolutionProducer>;

  @Field(() => String)
  message: Maybe<string>;

  @Field(() => Int)
  total!: number;

  @Field(() => [String])
  startedForTasks!: string[];

  @Field(() => [String])
  createdForTasks!: string[];

  @Field(() => [LowConfRegion])
  lowConfRegions!: LowConfRegion[];

  @Field(() => [ErrorRegion])
  errorRegions!: ErrorRegion[];

  /** Task paths whose points this run changed — see `setSolutionPointAdjustments`. */
  @Field(() => [String])
  pointAdjustments!: string[];

  /**
   * `_id` of every task whose artifact content this RUN actually changed.
   *
   * A re-upload rebuilds the whole tree even when the teacher corrected one task, so "this artifact was
   * re-extracted" says nothing about what needs redoing downstream. This does: it is what lets a corrected
   * master solution regenerate the grading scheme for that task alone, and a changed grading scheme regrade
   * that task alone, instead of putting the whole exam through both.
   *
   * Written by `persistTaskArtifact`, which is the one layer every producer of these fields goes through.
   * Cleared when a run opens, so it always describes the current pass.
   */
  @Field(() => [String])
  changedTasks!: string[];

  /** This run repeats an artifact that was already produced once (a re-upload). */
  @Field(() => Boolean)
  reupload: Maybe<boolean>;
}

@Entity()
export class TaskExtractionProgress extends BaseExtractionProgress {
  static override _class = 'TaskExtractionProgress';
}

@Entity()
export class AnswerExtractionProgress extends BaseExtractionProgress {
  static override _class = 'AnswerExtractionProgress';

  @Field(() => [String])
  errorSubmissions!: string[];
}
